import { useEffect, useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Typography,
  Button,
  Descriptions,
  Spin,
  message,
  Row,
  Col,
  Tag,
  Space,
  Divider,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
} from "antd";
import { ThemeContext } from "../../context/ThemeContext";
import {
  ArrowLeftOutlined,
  BankOutlined,
  UserOutlined,
  GlobalOutlined,
  ReloadOutlined,
  BarChartOutlined,
  CopyOutlined,
  EditOutlined,
  DownloadOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import apiClient from "../../services/apiClient";
import dayjs from "dayjs";
import CreditRatingsTable from "../LandingPages/CreditRatingsTable";
import "./IssuerDetails.css";
import "../Admin/AdminPages.css";

const { Title, Text } = Typography;

function ISINDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark } = useContext(ThemeContext);

  const [isinData, setIsinData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [isinEditModalVisible, setIsinEditModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:8080/api/v1/sb";

  // Get ISIN from location state
  const isin = location.state?.isin;

  useEffect(() => {
    if (isin) {
      fetchISINDetails();
    } else {
      setError("No ISIN provided");
      setLoading(false);
    }
  }, [isin]);

  const fetchISINDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "application/json",
      };

      const response = await apiClient.get(
        `${baseUrl}/master-isin/details/${encodeURIComponent(isin)}`,
        { headers, validateStatus: () => true }
      );

      if (response.data && response.data.statusCode === 200) {
        setIsinData(response.data.data);
      } else {
        throw new Error(response.data?.message || "Failed to fetch ISIN details");
      }
    } catch (error) {
      setError("Error fetching ISIN details");
      message.error("Error fetching ISIN details");
      console.error("Error fetching ISIN details:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format field values
  const formatFieldValue = (value) => {
    if (value === null || value === undefined || value === "" || value === "null") {
      return "N/A";
    }
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(", ") : "N/A";
    }
    if (typeof value === "object") {
      return JSON.stringify(value);
    }
    return value;
  };

  // Helper function to format currency values
  const formatCurrency = (value) => {
    if (!value || value === "N/A") return "N/A";
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numValue);
  };

  // Helper function to format percentage values
  const formatPercentage = (value) => {
    if (!value || value === "N/A") return "N/A";
    return `${value}%`;
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleRefresh = () => {
    if (isin) {
      fetchISINDetails();
    }
  };

  // Handle copy ISIN to clipboard
  const handleCopyISIN = () => {
    navigator.clipboard.writeText(isin).then(() => {
      message.success('ISIN copied to clipboard!');
    }).catch(() => {
      message.error('Failed to copy ISIN');
    });
  };

  // Handle edit ISIN
  const handleEditISIN = async () => {
    setEditLoading(true);
    setIsinEditModalVisible(true);
    try {
      const headers = {
        "Content-Type": "application/json",
      };
      const response = await apiClient.get(
        `${baseUrl}/master-isin/variable-data/${encodeURIComponent(isin)}`,
        { headers, validateStatus: () => true }
      );
      if (response.data && response.data.statusCode === 200 && response.data.data) {
        setEditData(response.data.data);
        editForm.setFieldsValue(response.data.data);
      } else {
        setEditData(null);
        message.error("Failed to fetch ISIN data for editing.");
        setIsinEditModalVisible(false);
      }
    } catch (err) {
      setEditData(null);
      message.error("Error fetching ISIN data for editing.");
      setIsinEditModalVisible(false);
    } finally {
      setEditLoading(false);
    }
  };

  // Handle download ISIN
  const handleDownloadISIN = () => {
    message.info('Download functionality coming soon');
  };

  // Handle update submit
  const handleUpdate = async () => {
    try {
      let values = await editForm.validateFields();
      delete values.createdAt;
      delete values.updatedAt;
      setEditLoading(true);
      const headers = {
        "Content-Type": "application/json",
      };
      const response = await apiClient.put(
        `${baseUrl}/master-isin/${encodeURIComponent(editData.isin)}`,
        values,
        { headers, validateStatus: () => true }
      );
      if (response.data && response.data.statusCode === 200) {
        message.success("ISIN updated successfully.");
        setIsinEditModalVisible(false);
        // Refresh the ISIN details
        fetchISINDetails();
      } else {
        message.error(response.data?.message || "Failed to update ISIN.");
      }
    } catch (err) {
      message.error("Error updating ISIN.");
    } finally {
      setEditLoading(false);
    }
  };

  // Handle edit credit rating
  const handleEditCreditRating = (record) => {
    console.log("Edit credit rating:", record);
    setEditingRecord(record);
    setEditModalVisible(true);

    // Populate form with existing data
    form.setFieldsValue({
      creditRatingAgencyName: record.creditRatingAgencyName || '',
      currentRating: record.currentRating || '',
      outlook: record.outlook || '',
      ratingAction: record.ratingAction || '',
      creditRatingDate: record.creditRatingDate ? dayjs(record.creditRatingDate) : null,
      reviewDueDate: record.reviewDueDate ? dayjs(record.reviewDueDate) : null,
      ratingChangeDate: record.ratingChangeDate ? dayjs(record.ratingChangeDate) : null,
      verificationStatus: record.verificationStatus || '',
      dateOfVerification: record.dateOfVerification ? dayjs(record.dateOfVerification) : null,
      iciWatch: record.iciWatch || '',
      pressReleaseLink: record.pressReleaseLink || '',
      provisionalFlag: record.provisionalFlag || '',
    });
  };

  // Handle delete credit rating
  const handleDeleteCreditRating = (record) => {
    console.log("Delete credit rating:", record);

    Modal.confirm({
      title: 'Delete Credit Rating',
      content: `Are you sure you want to delete the ${record.creditRatingAgencyName} rating (${record.currentRating})?`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const headers = {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "application/json",
          };

          // API call to delete credit rating
          const response = await apiClient.delete(
            `${baseUrl}/master-isin/credit-ratings/${record.id}`,
            { headers, validateStatus: () => true }
          );

          if (response.data && response.data.statusCode === 200) {
            message.success("Credit rating deleted successfully!");
            // Trigger a re-render of the CreditRatingsTable
            setIsinData(prev => ({ ...prev, lastUpdated: Date.now() }));
          } else {
            message.error(response.data?.message || "Failed to delete credit rating");
          }
        } catch (error) {
          console.error("Error deleting credit rating:", error);
          message.error("Failed to delete credit rating. Please try again.");
        }
      },
    });
  };

  // Handle modal close
  const handleModalClose = () => {
    setEditModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  // Handle create credit rating
  const handleCreateCreditRating = () => {
    setCreateModalVisible(true);
    createForm.resetFields();
  };

  // Handle create modal close
  const handleCreateModalClose = () => {
    setCreateModalVisible(false);
    createForm.resetFields();
  };

  // Handle create form submit
  const handleCreateFormSubmit = async (values) => {
    try {
      console.log("Create form values:", values);

      // Format dates for API and prepare complete payload
      const formattedValues = {
        isin: isin, // Current ISIN
        isinDesc: isinData?.nameOfTheInstrument || '', // ISIN description
        issuerId: isinData?.corporateIdentityNumber || '', // CIN as issuer ID
        issuerName: isinData?.companyIssuerName || '', // Company name
        creditRatingAgencyName: values.creditRatingAgencyName,
        currentRating: values.currentRating,
        outlook: values.outlook || 'Stable',
        ratingAction: values.ratingAction || 'New Rating',
        creditRatingDate: values.creditRatingDate ? values.creditRatingDate.format('YYYY-MM-DD') : null,
        reviewDueDate: values.reviewDueDate ? values.reviewDueDate.format('YYYY-MM-DD') : null,
        ratingChangeDate: values.ratingChangeDate ? values.ratingChangeDate.format('YYYY-MM-DD') : null,
        updateDate: new Date().toISOString().split('T')[0], // Current date
        verificationStatus: values.verificationStatus || 'Pending',
        dateOfVerification: values.dateOfVerification ? values.dateOfVerification.format('YYYY-MM-DD') : null,
        iciWatch: values.iciWatch || '',
        datasource: 'Manual Entry',
        pressReleaseLink: values.pressReleaseLink || '',
        provisionalFlag: values.provisionalFlag || 'N',
        ratingType: 'CURRENT', // Set as current rating
      };

      const headers = {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "application/json",
      };

      // API call to create credit rating
      const response = await apiClient.post(
        `${baseUrl}/master-isin/credit-ratings`,
        formattedValues,
        { headers, validateStatus: () => true }
      );

      if (response.data && response.data.statusCode === 201) {
        message.success("Credit rating created successfully!");
        handleCreateModalClose();
        // Trigger a re-render of the CreditRatingsTable by updating a key or state
        setIsinData(prev => ({ ...prev, lastUpdated: Date.now() }));
      } else {
        message.error(response.data?.message || "Failed to create credit rating");
      }
    } catch (error) {
      console.error("Error creating credit rating:", error);
      message.error("Failed to create credit rating. Please try again.");
    }
  };

  // Handle form submit (Update)
  const handleFormSubmit = async (values) => {
    try {
      console.log("Form values:", values);
      console.log("Editing record:", editingRecord);

      // Format dates for API and prepare complete payload
      const formattedValues = {
        isin: editingRecord.isin || isin,
        isinDesc: editingRecord.isinDesc || isinData?.nameOfTheInstrument || '',
        issuerId: editingRecord.issuerId || isinData?.corporateIdentityNumber || '',
        issuerName: editingRecord.issuerName || isinData?.companyIssuerName || '',
        creditRatingAgencyName: values.creditRatingAgencyName,
        currentRating: values.currentRating,
        outlook: values.outlook || editingRecord.outlook || 'Stable',
        ratingAction: values.ratingAction,
        creditRatingDate: values.creditRatingDate ? values.creditRatingDate.format('YYYY-MM-DD') : editingRecord.creditRatingDate,
        reviewDueDate: values.reviewDueDate ? values.reviewDueDate.format('YYYY-MM-DD') : editingRecord.reviewDueDate,
        ratingChangeDate: values.ratingChangeDate ? values.ratingChangeDate.format('YYYY-MM-DD') : editingRecord.ratingChangeDate,
        updateDate: new Date().toISOString().split('T')[0], // Current date
        verificationStatus: values.verificationStatus,
        dateOfVerification: values.dateOfVerification ? values.dateOfVerification.format('YYYY-MM-DD') : editingRecord.dateOfVerification,
        iciWatch: values.iciWatch || editingRecord.iciWatch || '',
        datasource: editingRecord.datasource || 'Manual Entry',
        pressReleaseLink: values.pressReleaseLink || editingRecord.pressReleaseLink || '',
        provisionalFlag: values.provisionalFlag || editingRecord.provisionalFlag || 'N',
        ratingType: editingRecord.ratingType || 'CURRENT',
      };

      const headers = {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "application/json",
      };

      // API call to update credit rating
      const response = await apiClient.put(
        `${baseUrl}/master-isin/credit-ratings/${editingRecord.id}`,
        formattedValues,
        { headers, validateStatus: () => true }
      );

      if (response.data && response.data.statusCode === 200) {
        message.success("Credit rating updated successfully!");
        handleModalClose();
        // Trigger a re-render of the CreditRatingsTable
        setIsinData(prev => ({ ...prev, lastUpdated: Date.now() }));
      } else {
        message.error(response.data?.message || "Failed to update credit rating");
      }
    } catch (error) {
      console.error("Error updating credit rating:", error);
      message.error("Failed to update credit rating. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className={`admin-page ${isDark ? "dark" : ""}`}>
        <div className="loading-container">
          <Spin size="large" />
          <Text>Loading ISIN details...</Text>
        </div>
      </div>
    );
  }

  if (error || !isinData) {
    return (
      <div className={`admin-page ${isDark ? "dark" : ""}`}>
        <div className="error-container">
          <Title level={3}>Error</Title>
          <Text>{error || "ISIN not found"}</Text>
          <Button type="primary" onClick={handleGoBack} style={{ marginTop: 16 }}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Stats for hero section
  const stats = [
    {
      number: formatFieldValue(isinData.faceValue) || "N/A",
      label: "Face Value",
      icon: <BankOutlined />,
    },
    {
      number: formatFieldValue(isinData.issueSize) || "N/A",
      label: "Issue Size",
      icon: <BarChartOutlined />,
    },
    {
      number: formatFieldValue(isinData.allotmentDate) || "N/A",
      label: "Allotment Date",
      icon: <GlobalOutlined />,
    },
    {
      number: formatFieldValue(isinData.stockExchange) || "N/A",
      label: "Stock Exchange",
      icon: <UserOutlined />,
    },
  ];

  return (
    <div className={`admin-page ${isDark ? "dark" : ""}`} style={{ background: '#f8fafc', minHeight: '100vh', padding: '2rem 0 4rem 0' }}>
      {/* Hero Section */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={handleGoBack}
            style={{ 
              color: isDark ? '#fff' : '#1a1a1a',
              fontWeight: 500,
              padding: '8px 16px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            Back
          </Button>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '20px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          padding: '3rem 2.5rem',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <Title level={1} style={{
              fontSize: '48px',
              fontWeight: 800,
              color: '#ffffff',
              marginBottom: '1rem',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
            }}>
              {formatFieldValue(isinData.companyIssuerName)}
            </Title>
            <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <Tag
                style={{
                  fontSize: '18px',
                  padding: '10px 20px',
                  borderRadius: '12px',
                  fontFamily: 'monospace',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  color: '#ffffff',
                  fontWeight: 600,
                  transition: 'all 0.3s ease'
                }}
                onClick={handleCopyISIN}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {formatFieldValue(isinData.isin)}
                <CopyOutlined />
              </Tag>
              {isinData.instrumentName && (
                <Tag style={{
                  fontSize: '14px',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  fontWeight: 500
                }}>
                  {formatFieldValue(isinData.nameOfTheInstrument)}
                </Tag>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards Section */}
      <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} md={6}>
          <Card
            className="modern-section-card"
            style={{
              textAlign: 'center',
              borderRadius: '16px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e8ecf0',
              transition: 'all 0.3s ease',
              height: '100%'
            }}
            bodyStyle={{ padding: '24px' }}
            hoverable
          >
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '12px'
              }}>
                <BankOutlined style={{ fontSize: '24px', color: '#ffffff' }} />
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 14, fontWeight: 500, color: '#64748b' }}>
                Face Value
              </Text>
            </div>
            <div>
              <Text strong style={{ fontSize: 28, fontWeight: 700, color: isDark ? '#ffffff' : '#1a1a1a' }}>
                {formatCurrency(isinData.faceValue)}
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            className="modern-section-card"
            style={{
              textAlign: 'center',
              borderRadius: '16px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e8ecf0',
              transition: 'all 0.3s ease',
              height: '100%'
            }}
            bodyStyle={{ padding: '24px' }}
            hoverable
          >
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '12px'
              }}>
                <BarChartOutlined style={{ fontSize: '24px', color: '#ffffff' }} />
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 14, fontWeight: 500, color: '#64748b' }}>
                Coupon Rate
              </Text>
            </div>
            <div>
              <Text strong style={{ fontSize: 28, fontWeight: 700, color: isDark ? '#ffffff' : '#1a1a1a' }}>
                {formatPercentage(isinData.couponRate)}
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            className="modern-section-card"
            style={{
              textAlign: 'center',
              borderRadius: '16px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e8ecf0',
              transition: 'all 0.3s ease',
              height: '100%'
            }}
            bodyStyle={{ padding: '24px' }}
            hoverable
          >
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '12px'
              }}>
                <DollarOutlined style={{ fontSize: '24px', color: '#ffffff' }} />
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 14, fontWeight: 500, color: '#64748b' }}>
                Amount Raised
              </Text>
            </div>
            <div>
              <Text strong style={{ fontSize: 28, fontWeight: 700, color: isDark ? '#ffffff' : '#1a1a1a' }}>
                {formatCurrency(isinData.amountRaised)}
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            className="modern-section-card"
            style={{
              textAlign: 'center',
              borderRadius: '16px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e8ecf0',
              transition: 'all 0.3s ease',
              height: '100%'
            }}
            bodyStyle={{ padding: '24px' }}
            hoverable
          >
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '12px'
              }}>
                <GlobalOutlined style={{ fontSize: '24px', color: '#ffffff' }} />
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 14, fontWeight: 500, color: '#64748b' }}>
                Security
              </Text>
            </div>
            <div>
              <Text strong style={{ fontSize: 28, fontWeight: 700, color: isDark ? '#ffffff' : '#1a1a1a' }}>
                {formatFieldValue(isinData.security)}
              </Text>
            </div>
          </Card>
        </Col>
      </Row>



      {/* Main Content */}
      <Row gutter={[24, 24]}>
        {/* ISIN Details Card */}
        <Col xs={24} lg={24}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <BankOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
                <Title level={4} style={{ margin: 0, fontWeight: 600 }}>ISIN Information</Title>
              </div>
            }
            className="modern-section-card"
            style={{
              borderRadius: '16px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e8ecf0'
            }}
            extra={
              <Space>
                <Button
                  icon={<EditOutlined />}
                  onClick={handleEditISIN}
                  style={{
                    backgroundColor: '#667eea',
                    borderColor: '#667eea',
                    color: 'white',
                    borderRadius: '8px',
                    fontWeight: 500,
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#5568d3';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#667eea';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                  }}
                >
                  Edit
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadISIN}
                  style={{
                    backgroundColor: '#1890ff',
                    borderColor: '#1890ff',
                    color: 'white',
                    borderRadius: '8px',
                    fontWeight: 500,
                    boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#40a9ff';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#1890ff';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(24, 144, 255, 0.3)';
                  }}
                >
                  Download
                </Button>
              </Space>
            }
          >
            <Descriptions
              bordered
              column={1}
              size="middle"
              labelStyle={{ 
                fontWeight: 600, 
                width: 300,
                background: isDark ? '#3a3a4e' : '#f8fafc',
                color: isDark ? '#ffffff' : '#1a1a1a'
              }}
              contentStyle={{ 
                wordBreak: "break-word",
                background: isDark ? '#2a2a3e' : '#ffffff',
                color: isDark ? '#ffffff' : '#1a1a1a'
              }}
            >
              <Descriptions.Item label="ISIN Code">
                <Text strong style={{ fontFamily: "monospace" }}>
                  {formatFieldValue(isinData.isin)}
                </Text>
              </Descriptions.Item>

              <Descriptions.Item label="Corporate Identity Number">
                {formatFieldValue(isinData.corporateIdentityNumber)}
              </Descriptions.Item>

              <Descriptions.Item label="Legal Entity Identifier">
                {formatFieldValue(isinData.legalEntityIdentifier)}
              </Descriptions.Item>

              <Descriptions.Item label="Instrument Name">
                {formatFieldValue(isinData.nameOfTheInstrument)}
              </Descriptions.Item>

              <Descriptions.Item label="Instrument Description">
                {formatFieldValue(isinData.descriptionOfInstrument)}
              </Descriptions.Item>

              <Descriptions.Item label="Type of Instrument">
                <Tag color="blue">{formatFieldValue(isinData.typeOfInstrument)}</Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Face Value">
                {formatCurrency(isinData.faceValue)}
              </Descriptions.Item>

              <Descriptions.Item label="Issue Price">
                {formatCurrency(isinData.issuePrice)}
              </Descriptions.Item>

              <Descriptions.Item label="Issue Size">
                {formatFieldValue(isinData.issueSize)}
              </Descriptions.Item>

              <Descriptions.Item label="Amount Raised">
                {formatCurrency(isinData.amountRaised)}
              </Descriptions.Item>

              <Descriptions.Item label="Allotment Date">
                {formatFieldValue(isinData.allotmentDate)}
              </Descriptions.Item>

              <Descriptions.Item label="Redemption Date">
                {formatFieldValue(isinData.redemption)}
              </Descriptions.Item>

              <Descriptions.Item label="Coupon Rate">
                {formatPercentage(isinData.couponRate)}
              </Descriptions.Item>

              <Descriptions.Item label="Coupon Type">
                {formatFieldValue(isinData.couponType)}
              </Descriptions.Item>

              <Descriptions.Item label="Coupon Payment Frequency">
                {formatFieldValue(isinData.couponPaymentFrequency)}
              </Descriptions.Item>

              <Descriptions.Item label="Security">
                <Tag color="green">{formatFieldValue(isinData.security)}</Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Seniority">
                {formatFieldValue(isinData.seniorityInRepayment)}
              </Descriptions.Item>

              <Descriptions.Item label="Currency">
                <Tag color="gold">{formatFieldValue(isinData.currencyOfIssuance)}</Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Mode of Issue">
                {formatFieldValue(isinData.modeOfIssue)}
              </Descriptions.Item>

              <Descriptions.Item label="Sector">
                {formatFieldValue(isinData.sector)}
              </Descriptions.Item>

              <Descriptions.Item label="Industry">
                {formatFieldValue(isinData.industry)}
              </Descriptions.Item>

              <Descriptions.Item label="Type of Issuer">
                {formatFieldValue(isinData.typeOfIssuerBasedOnOwnership)}
              </Descriptions.Item>

              <Descriptions.Item label="Object of Issue">
                <div style={{ maxHeight: "100px", overflowY: "auto" }}>
                  {formatFieldValue(isinData.objectOfIssue)}
                </div>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

      </Row>

      {/* Credit Ratings Section */}
      <div style={{ marginTop: 32 }}>
        <Card
          className="modern-section-card"
          style={{
            borderRadius: '16px',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e8ecf0'
          }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <BarChartOutlined style={{ fontSize: '20px', color: '#faad14' }} />
              <Title level={4} style={{ margin: 0, fontWeight: 600 }}>Credit Ratings</Title>
            </div>
          }
          extra={
            <Button
              type="primary"
              onClick={handleCreateCreditRating}
              style={{ 
                fontWeight: 600,
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(24, 144, 255, 0.3)';
              }}
            >
              Add Credit Rating
            </Button>
          }
        >
          <CreditRatingsTable
            isin={isinData.isin}
            showActions={true}
            onEdit={handleEditCreditRating}
            onDelete={handleDeleteCreditRating}
            key={isinData.lastUpdated} // Force re-render when data changes
          />
        </Card>
      </div>

      {/* Edit ISIN Modal */}
      <Modal
        title={`Edit ISIN - ${editData?.isin || ""}`}
        open={isinEditModalVisible}
        onCancel={() => setIsinEditModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsinEditModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="update" type="primary" loading={editLoading} onClick={handleUpdate}>
            Update
          </Button>,
        ]}
        width={800}
        destroyOnClose
      >
        {editLoading && !editData ? (
          <Spin size="large" />
        ) : (
          <Form
            form={editForm}
            layout="vertical"
            initialValues={editData}
            disabled={editLoading}
          >
            {editData && (() => {
              const fields = Object.entries(editData).filter(([key]) => key !== 'createdAt' && key !== 'updatedAt');
              const items = [];
              for (let i = 0; i < fields.length; i += 2) {
                const [key1, value1] = fields[i];
                const [key2, value2] = fields[i + 1] || [];
                items.push(
                  <div key={key1 + (key2 || '')} style={{ display: 'flex', width: '100%', gap: 16 }}>
                    <Form.Item
                      key={key1}
                      label={key1.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                      name={key1}
                      rules={key1 === "isin" ? [] : [{ required: false }]}
                      style={{ flex: 1 }}
                    >
                      {key1 === "isin" ? <Input disabled /> : <Input />}
                    </Form.Item>
                    {key2 && (
                      <Form.Item
                        key={key2}
                        label={key2.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                        name={key2}
                        rules={key2 === "isin" ? [] : [{ required: false }]}
                        style={{ flex: 1 }}
                      >
                        {key2 === "isin" ? <Input disabled /> : <Input />}
                      </Form.Item>
                    )}
                  </div>
                );
              }
              return items;
            })()}
          </Form>
        )}
      </Modal>

      {/* Edit Credit Rating Modal */}
      <Modal
        title="Edit Credit Rating"
        open={editModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Credit Rating Agency Name"
                name="creditRatingAgencyName"
                rules={[{ required: true, message: 'Please enter agency name' }]}
              >
                <Input placeholder="Enter agency name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Credit Rating"
                name="currentRating"
                rules={[{ required: true, message: 'Please enter credit rating' }]}
              >
                <Input placeholder="e.g., AAA, AA+, BBB" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Outlook"
                name="outlook"
              >
                <Select placeholder="Select outlook" allowClear>
                  <Select.Option value="Positive">Positive</Select.Option>
                  <Select.Option value="Stable">Stable</Select.Option>
                  <Select.Option value="Negative">Negative</Select.Option>
                  <Select.Option value="Developing">Developing</Select.Option>
                  <Select.Option value="Watch">Watch</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Rating Action"
                name="ratingAction"
              >
                <Select placeholder="Select rating action" allowClear>
                  <Select.Option value="Upgrade">Upgrade</Select.Option>
                  <Select.Option value="Downgrade">Downgrade</Select.Option>
                  <Select.Option value="Affirm">Affirm</Select.Option>
                  <Select.Option value="Withdraw">Withdraw</Select.Option>
                  <Select.Option value="Place on Watch">Place on Watch</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Verification Status"
                name="verificationStatus"
              >
                <Select placeholder="Select verification status" allowClear>
                  <Select.Option value="Verified">Verified</Select.Option>
                  <Select.Option value="Pending">Pending</Select.Option>
                  <Select.Option value="Failed">Failed</Select.Option>
                  <Select.Option value="Not Verified">Not Verified</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Initial Credit Rating Date"
                name="creditRatingDate"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select date"
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Review Due Date"
                name="reviewDueDate"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select date"
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Date of Rating Change"
                name="ratingChangeDate"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select date"
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Date of Verification"
                name="dateOfVerification"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select date"
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="ICI Watch"
                name="iciWatch"
              >
                <Input placeholder="Enter ICI Watch status" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Press Release Link"
                name="pressReleaseLink"
              >
                <Input placeholder="Enter press release URL" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Provisional Flag"
                name="provisionalFlag"
              >
                <Select placeholder="Select provisional flag" allowClear>
                  <Select.Option value="Y">Yes</Select.Option>
                  <Select.Option value="N">No</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleModalClose}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Update Credit Rating
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Create Credit Rating Modal */}
      <Modal
        title="Create Credit Rating"
        open={createModalVisible}
        onCancel={handleCreateModalClose}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateFormSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Credit Rating Agency Name"
                name="creditRatingAgencyName"
                rules={[{ required: true, message: 'Please enter agency name' }]}
              >
                <Select placeholder="Select or enter agency name" allowClear showSearch>
                  <Select.Option value="CRISIL">CRISIL</Select.Option>
                  <Select.Option value="ICRA">ICRA</Select.Option>
                  <Select.Option value="CARE">CARE</Select.Option>
                  <Select.Option value="India Ratings">India Ratings</Select.Option>
                  <Select.Option value="Fitch">Fitch</Select.Option>
                  <Select.Option value="Moody's">Moody's</Select.Option>
                  <Select.Option value="S&P">S&P</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Credit Rating"
                name="currentRating"
                rules={[{ required: true, message: 'Please enter credit rating' }]}
              >
                <Select placeholder="Select or enter rating" allowClear showSearch>
                  <Select.Option value="AAA">AAA</Select.Option>
                  <Select.Option value="AA+">AA+</Select.Option>
                  <Select.Option value="AA">AA</Select.Option>
                  <Select.Option value="AA-">AA-</Select.Option>
                  <Select.Option value="A+">A+</Select.Option>
                  <Select.Option value="A">A</Select.Option>
                  <Select.Option value="A-">A-</Select.Option>
                  <Select.Option value="BBB+">BBB+</Select.Option>
                  <Select.Option value="BBB">BBB</Select.Option>
                  <Select.Option value="BBB-">BBB-</Select.Option>
                  <Select.Option value="BB+">BB+</Select.Option>
                  <Select.Option value="BB">BB</Select.Option>
                  <Select.Option value="BB-">BB-</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Outlook"
                name="outlook"
                initialValue="Stable"
              >
                <Select placeholder="Select outlook" allowClear>
                  <Select.Option value="Positive">Positive</Select.Option>
                  <Select.Option value="Stable">Stable</Select.Option>
                  <Select.Option value="Negative">Negative</Select.Option>
                  <Select.Option value="Developing">Developing</Select.Option>
                  <Select.Option value="Watch">Watch</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Rating Action"
                name="ratingAction"
                initialValue="New Rating"
              >
                <Select placeholder="Select rating action" allowClear>
                  <Select.Option value="New Rating">New Rating</Select.Option>
                  <Select.Option value="Upgrade">Upgrade</Select.Option>
                  <Select.Option value="Downgrade">Downgrade</Select.Option>
                  <Select.Option value="Affirm">Affirm</Select.Option>
                  <Select.Option value="Withdraw">Withdraw</Select.Option>
                  <Select.Option value="Place on Watch">Place on Watch</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Verification Status"
                name="verificationStatus"
                initialValue="Pending"
              >
                <Select placeholder="Select verification status" allowClear>
                  <Select.Option value="Verified">Verified</Select.Option>
                  <Select.Option value="Pending">Pending</Select.Option>
                  <Select.Option value="Failed">Failed</Select.Option>
                  <Select.Option value="Not Verified">Not Verified</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Credit Rating Date"
                name="creditRatingDate"
                rules={[{ required: true, message: 'Please select rating date' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select date"
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Review Due Date"
                name="reviewDueDate"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select date"
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Rating Change Date"
                name="ratingChangeDate"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select date"
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Date of Verification"
                name="dateOfVerification"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select date"
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="ICI Watch"
                name="iciWatch"
              >
                <Input placeholder="Enter ICI Watch status" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Press Release Link"
                name="pressReleaseLink"
              >
                <Input placeholder="Enter press release URL" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Provisional Flag"
                name="provisionalFlag"
                initialValue="N"
              >
                <Select placeholder="Select provisional flag" allowClear>
                  <Select.Option value="Y">Yes</Select.Option>
                  <Select.Option value="N">No</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCreateModalClose}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create Credit Rating
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default ISINDetails;